import { Formik } from "formik";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box } from "../../../components/Box";
import { Button } from "../../../components/Button";
import { Flex } from "../../../components/Flex";
import { H2 } from "../../../components/Heading";
import { Label } from "../../../components/Label";
import { Paragraph } from "../../../components/Paragraph";
import Select from "../../../components/Select";
import AddressAutocomplete from "../../../components/AddressAutocomplete";
import { NewProfileFrame } from "../NewProfileFrame";
import { useCountries } from "../../../data/useCountries";
import useAddressLongForm from "../../../utils/forms/useAddressLongForm";
import { addressFormValidationSchema } from "../../../utils/validationSchema";
import { CheckAddress } from "../../../types/CheckAddress";
import { ROUTE } from "../../../routes";

type Option = {
  label: string;
  value: string | number;
  sac: boolean;
};

const Header = () => (
  <>
    <H2>Registered address</H2>
    <Paragraph>Please confirm the registered address or skip.</Paragraph>
  </>
);

const emptyAddress: CheckAddress = {
  building_number: "",
  flat_appartment_subbuilding: "",
  building_name: "",
  road_street: "",
  town_city: "",
  district: "",
  state_province_name: "",
  state_province_code: "",
  post_zip_code: "",
  country_code: "",
};

export const AddEntityAddress = () => {
  const navigate = useNavigate();
  const location = useLocation() as {
    state?: { newEntity?: any; registeredAddress?: CheckAddress };
  };

  const newEntity = location.state?.newEntity;
  const existingAddress = location.state?.registeredAddress;

  const { data } = useCountries();
  const { AddressLongForm } = useAddressLongForm();

  const countryOptions: Option[] = useMemo(
    () =>
      data?.data?.map(({ isoalpha2, name, sac }) => ({
        label: name,
        value: isoalpha2,
        sac,
      })) ?? [],
    [data],
  );

  const [selectedCountry, setSelectedCountry] = useState<Option | null>(null);
  const [selectedAddress, setSelectedAddress] = useState<any | null>(
    existingAddress ?? null,
  );
  const [formOpen, setFormOpen] = useState<boolean>(!!existingAddress);

  useEffect(() => {
    if (
      !existingAddress ||
      !existingAddress.country_code ||
      !countryOptions.length
    )
      return;

    const match = countryOptions.find(
      (opt) => String(opt.value) === String(existingAddress.country_code),
    );
    if (match) {
      setSelectedCountry(match);
    }
  }, [existingAddress, countryOptions]);

  const initialValues: CheckAddress = existingAddress ?? emptyAddress;

  const handleNext = (values: CheckAddress) => {
    if (!selectedCountry) return;

    const addressWithCountry: CheckAddress = {
      ...values,
      country_code: selectedCountry.value.toString(),
    };

    navigate(ROUTE.PROFILE_NEW_ENTITY_SUMMARY, {
      state: {
        newEntity,
        registeredAddress: addressWithCountry,
      },
    });
  };

  const handleSkip = () => {
    navigate(ROUTE.PROFILE_NEW_ENTITY_SUMMARY, {
      state: {
        newEntity,
        registeredAddress: undefined,
      },
    });
  };

  const handleBack = () => {
    navigate(ROUTE.PROFILE_NEW_ENTITY, {
      state: {
        newEntity,
      },
    });
  };

  return (
    <NewProfileFrame mode="entity" step="address">
      <Header />

      <Box position="relative" mt={3} mb={4}>
        <Select
          value={selectedCountry}
          options={countryOptions}
          onChange={(option: Option) => {
            setSelectedCountry(option);
            setSelectedAddress(null);
            setFormOpen(false);
          }}
          minWidth={200}
          width={600}
          flex={1}
          label="Country"
          name="country"
          isRequired
          hasError={!selectedCountry}
          errorMessage={selectedCountry ? "" : "Country is required"}
        />
      </Box>

      <Box width={600}>
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={addressFormValidationSchema}
          onSubmit={handleNext}
        >
          {(formik) => (
            <form onSubmit={formik.handleSubmit}>
              {selectedCountry && (
                <Box mt={2}>
                  <Flex alignItems="center" justifyContent="space-between">
                    <Label>Address</Label>
                    <Button
                      variant="text"
                      type="button"
                      onClick={() => setFormOpen(true)}
                    >
                      Enter address manually
                    </Button>
                  </Flex>

                  <AddressAutocomplete
                    setSelectedAddress={(address: any) => {
                      setSelectedAddress(address);
                      setFormOpen(true);
                      Object.keys(address).forEach((key) => {
                        formik.setFieldValue(key, (address as any)[key]);
                      });
                    }}
                    country={selectedCountry.value}
                    setFormOpen={setFormOpen}
                  />
                </Box>
              )}

              {formOpen && <AddressLongForm formik={formik} />}

              <Flex justifyContent="space-between" my={3}>
                <Button type="button" variant="text" onClick={handleBack}>
                  Back
                </Button>

                <Flex>
                  <Button
                    type="button"
                    variant="text"
                    mr={2}
                    onClick={handleSkip}
                  >
                    Skip
                  </Button>
                  <Button
                    type="submit"
                    isDisabled={
                      !selectedCountry ||
                      (!selectedAddress && !(formik.isValid && formik.dirty))
                    }
                  >
                    Next
                  </Button>
                </Flex>
              </Flex>
            </form>
          )}
        </Formik>
      </Box>
    </NewProfileFrame>
  );
};

export default AddEntityAddress;
